import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/admin-shell";

import { useConnection } from "@/lib/offline/connection";
import { offlineDB, estimateStorage } from "@/lib/offline/db";
import { countAIDrafts } from "@/lib/offline/ai-drafts";
import { listOutbox } from "@/lib/offline/outbox";
import { getLastSyncAt, syncAll } from "@/lib/offline/sync-engine";
import { Wifi, WifiOff, CloudUpload, HardDrive, Brain, RefreshCw, ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/offline")({
  ssr: false,
  head: () => ({ meta: [{ title: "Offline Health — MAKAI Admin" }] }),
  component: AdminOfflinePage,
});

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
function fmtAgo(ts: number | null) {
  if (!ts) return "Never";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function AdminOfflinePage() {

  const { state, pending } = useConnection();
  const [outboxCount, setOutboxCount] = useState(0);
  const [downloadsCount, setDownloadsCount] = useState(0);
  const [drafts, setDrafts] = useState(0);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const ob = await listOutbox();
    setOutboxCount(ob.length);
    const db = offlineDB();
    setDownloadsCount(db ? await db.downloads.count() : 0);
    setDrafts(await countAIDrafts());
    setStorage(await estimateStorage());
    setLastSync(await getLastSyncAt());
  }

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("makai:aidraft-changed", handler);
    const i = setInterval(refresh, 5000);
    return () => { window.removeEventListener("makai:aidraft-changed", handler); clearInterval(i); };
  }, []);

  const health = state === "offline"
    ? "Offline — changes are being saved locally."
    : pending > 0 || drafts > 0
      ? "Syncing pending items…"
      : "All data is synced.";

  return (
    <AdminShell title="Offline Health">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Link to="/admin" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
        <h1 className="text-3xl font-bold tracking-tight">Offline Health</h1>
        <p className="mt-2 text-muted-foreground">
          Connectivity, sync queue and local storage for this device. Each device tracks its own queue independently.
        </p>

        <div className={`mt-6 flex items-center justify-between rounded-2xl border p-5 ${state === "offline" ? "bg-amber-50 border-amber-200" : "bg-card"}`}>
          <div className="flex items-center gap-3">
            {state === "offline" ? <WifiOff className="h-6 w-6 text-amber-600" /> : <Wifi className="h-6 w-6 text-emerald-600" />}
            <div>
              <div className="font-semibold capitalize">{state}</div>
              <div className="text-sm text-muted-foreground">{health}</div>
            </div>
          </div>
          <Button onClick={async () => { setBusy(true); try { await syncAll(); await refresh(); } finally { setBusy(false); } }} disabled={busy || state === "offline"}>
            <RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sync now
          </Button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={<CloudUpload className="h-5 w-5" />} label="Pending writes" value={outboxCount} hint="Queued mutations" />
          <Stat icon={<Brain className="h-5 w-5" />} label="Queued AI prompts" value={drafts} hint="Will send when online" />
          <Stat icon={<HardDrive className="h-5 w-5" />} label="Cached resources" value={downloadsCount} hint="Lessons & library items" />
          <Stat icon={<RefreshCw className="h-5 w-5" />} label="Last full sync" value={fmtAgo(lastSync)} hint="On this device" />
        </div>

        {storage && (
          <div className="mt-6 rounded-2xl border bg-card p-5">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Local storage</span>
              <span className="text-muted-foreground">{fmtBytes(storage.usage)} of {fmtBytes(storage.quota)}</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary" style={{ width: `${Math.min(100, (storage.usage / Math.max(1, storage.quota)) * 100)}%` }} />
            </div>
          </div>
        )}

        {(outboxCount > 0 || drafts > 0) && state === "offline" && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
            <div>
              <div className="font-semibold">Working offline</div>
              <p>Saved changes and AI prompts will replay automatically when this device reconnects. You can keep working safely.</p>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Tip for school-wide visibility</p>
          <p className="mt-1">For aggregated multi-device telemetry across all staff and learners, deploy the MAKAI mobile companion app — it reports anonymised sync metrics to this dashboard. Currently this view reflects your own browser session.</p>
        </div>
      </div>
    </AdminShell>
  );
}

function Stat({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint: string }) {
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">{icon}<span className="text-xs uppercase tracking-wide">{label}</span></div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}
